
<template>
    <div>
        <div class="flex items-center my-4">
            <BarChartIcon class="text-primary-light w-10 mr-1" />
            <h1 class="text-black text-xl lg:text-2xl">Practice Performance</h1>
        </div>

        <div class="items-center flex grow-0 mb-6">
            <div class="mx-4 flex items-center">
                <div class="min-w-100 max-w-100 mx-2">
                    <v-select
                        ref="selectedState"
                        label="State"
                        hide-details
                        dense
                        :disabled="isDisabledState"
                        :ripple="false"
                        :items="stateList"
                        @focusin.prevent="setFilterType('state')"
                        @change="filterUpdated"
                    ></v-select>
                </div>

            </div>

            <div class="min-w-200 max-w-200 mx-4">
                <DataRangePicker
                    label="Survey Visit Date Range"
                    :max-date="today"
                    v-model="dateRange"
                    hide-details
                    dense
                    range
                    :ripple="false"
                />
            </div>
            <div class="mx-4">
                <v-btn
                    class="mr-2"
                    color="primary"
                    depressed
                    :disabled="isDisabledApply"
                    @click="handleApply()"
                >Apply</v-btn>
                <v-btn
                    class=""
                    color="primary"
                    depressed
                    :disabled="isDisabledReset"
                    @focusin.prevent="setFilterType('reset')"
                    @click="filterUpdated"
                >RESET</v-btn>
            </div>
        </div>
        <div>
            <div v-if="errorCode === 404"
                class="text-center"
            >
                <h1 class="text-black text-lg lg:text-2xl text-center mb-3 mt-4">Unable to generate this report for the specified filters due to insufficient data!
                </h1>
                <p>Please select another set of filters and try again.</p>
            </div>
            <div class="flex items-center justify-center h-20 w-full" v-else-if="loading">
                <Loader class="w-12 h-12 text-primary-dark" />
            </div> 
            <div v-else-if="practiceList">
                  <PracticeNonConformities :practiceList="processedPracticeList" :title="title"/>
            </div> 
            <div v-else>
                <p class="text-sm text-grey-1 text-center">Select a state and a date range of survey visits.</p>
            </div> 
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import BarChartIcon from '@/assets/icons/icons_barcharts.svg'
import DataRangePicker from '@/components/partials/DateRangePicker'
import PracticeNonConformities from '@/components/analytics-report/partials/PracticeNonConformities'
import {format} from 'date-fns'
import camelcaseKeys from 'camelcase-keys'
import Loader from '@/assets/loader.svg'

export default {
    name: 'PracticePerformance',
    components: {DataRangePicker, BarChartIcon, PracticeNonConformities, Loader},
    data(){
        return {
            firstDay: format(new Date(new Date().getFullYear(), 0), 'Y-MM-dd'),
            lastDay: format(new Date(), 'Y-MM-dd'),
            errorCode: null,
            loading: false, 
            dataFilter: {},
            practiceList: null,
            filterType: null,
            listFilterType: [],
            filter: {},
            title: '',
            today: format(new Date(),'yyyy-MM-dd'),
            dateRange: [],
            defaultRange: true,
            isDisabledApply: true,
            isDisabledReset: true,
            isDisabledState: false,
        }
    },
    mounted() { 
        this.dateRange = [
            this.firstDay,
            this.lastDay
        ]
    },
    computed: {
        ...mapState({
            staticChoices: state => state.app.staticChoices
        }),
        stateList() {
          const allOption = { value: null, label: 'All States', text: 'All States' }
          const states = this.staticChoices['analytics_state'] || []
          return [allOption, ...states]
        },
        processedPracticeList() {
          return this.practiceList.map(item => ({
            ...item,
            high: item.high ?? 0,
            critical: item.critical ?? 0,
            moderate: item.moderate ?? 0,
            low: item.low ?? 0,
          }))
        }
    },
    methods: {
        handleApply() {
            this.loading = true
            this.$http({
                method: 'POST',
                url: `/reports/analytics/practice_non_conformities/`,
                data: this.filter
            }).then(({data}) => {
                this.practiceList = camelcaseKeys(data, {deep: true})
                this.errorCode = null
            })
            .catch(({ response }) => {
                if(response?.status === 404 || response?.status === 500){
                    this.errorCode = 404
                    this.loaded = false
                }
            })
            .finally(() => {
                this.loading = false
            })
        },
        setFilterType(type) {
            this.filterType = type
        },
        filterUpdated(item) {
            const arrType = ['state']
            if(this.filterType === "reset"){
                this.isDisabledReset = this.filterType === "reset" ? true : false
                this.$refs["selectedState"].reset()
                this.dateRange = [
                    this.firstDay,
                    this.lastDay
                ]
                this.filter= {  }
                this.listFilterType = []
                this.isDisabledState = false
                this.defaultRange = true
            }else if(arrType.indexOf(this.filterType) >= 0 ){
                
                this.isDisabledReset = false
                // Remove value contained in this.listFilterType
                arrType.map((value) => {
                    if(this.listFilterType.indexOf(value) >= 0){
                        this.listFilterType.splice(this.listFilterType.indexOf(value), 1);
                    }
                })
                this.listFilterType.push(this.filterType)

                switch(this.filterType) {

                    case 'state' :
                        this.filter.state = item
                        this.isDisabledState = false
                        break
                }
            }
        },
    },
    watch: {
        dateRange(value) {
            let listFilterType = this.listFilterType
            const index = listFilterType.indexOf('dateRange')
            if(index >= 0 ){
                listFilterType = listFilterType.splice(index, 1)
            }
            if(value.length === 2){
                this.listFilterType.push('dateRange')
                this.filter.date_start = format(new Date(value[0]),'yyyy-MM-dd')
                this.filter.date_end = format(new Date(value[1]),'yyyy-MM-dd')
                this.isDisabledReset = this.defaultRange? true :false
                this.defaultRange = false
            }
        },
        listFilterType(data){
            this.isDisabledApply = data.indexOf('dateRange') === -1
        }
    },
}
</script>
